import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type {
  BreakdownItem,
  RouteQueryParams as MarketParityInsightParams,
} from 'lumen/routes/market/parity/insight';
import type { Transition } from 'lumen/services/transition';
import type {
  MealTypeValues,
  MembershipType,
} from 'lumen/utils/constants/general-constants';
import { MEMBERSHIPTYPES } from 'lumen/utils/constants/general-constants';
import type {
  Breakdown,
  CountryCodeWithDefault,
  DateAggregation,
  ParityFilter,
  RateType,
} from 'lumen/utils/constants/pi-constant-values';
import { HISTORICAL_POINTS_AMOUNT } from 'lumen/utils/constants/pi-constant-values';
import { demoTransformTrendsData } from 'lumen/utils/parity/insight/demo-transform';
import { formatUrl } from 'lumen/utils/rates-request';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type TrendsDataRequestParams = {
  'aggregations[]': Breakdown;
  base_membership_type?: MembershipType;
  date_aggregation: DateAggregation;
  extract_date: string;
  filters?: string;
  los: number;
  market_id?: string | null;
  mealtype: MealTypeValues;
  periods: number;
  pos: CountryCodeWithDefault;
  rate_type: RateType;
  subscription_id?: string | null;
};

export type TrendsDataResponse = Record<
  Breakdown,
  BreakdownItemsPerExtractDate
>;

export type BreakdownItemsPerExtractDate = Record<string, BreakdownItem[]>;

export type RouteQueryParams = {
  segmentation: Breakdown;
  selection: ParityFilter;
  trendsExtractDate: string;
};

export default class MarketParityInsightTrendsRoute extends Route {
  queryParams = {
    segmentation: {
      refreshModel: true,
    },
    selection: {
      refreshModel: false,
    },
    trendsExtractDate: {
      refreshModel: true,
    },
  };

  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare transition: Services['transition'];

  fetchTrendsDataTask = task(async (params: RouteQueryParams) => {
    try {
      const {
        dateAggregation,
        extractDate,
        segmentationFilters,
        los,
        mealPlan,
        pos,
        rateType,
        showMemberRate,
      } = this.paramsFor('market.parity.insight') as MarketParityInsightParams;

      const requestParams: TrendsDataRequestParams = {
        'aggregations[]': params.segmentation,
        date_aggregation: dateAggregation,
        extract_date: params.trendsExtractDate ?? extractDate,
        market_id: this.hotelAndMarketService.hotelOrMarket.id,
        periods: HISTORICAL_POINTS_AMOUNT,
        los: los,
        mealtype: mealPlan,
        pos: pos,
        rate_type: rateType,
      };

      if (segmentationFilters.length) {
        requestParams.filters = segmentationFilters;
      }

      if (showMemberRate) {
        requestParams.base_membership_type = MEMBERSHIPTYPES.MEMBER_ONLY;
      }

      const trendsData: TrendsDataResponse = await this.ajax.request(
        formatUrl(
          '/apigateway/v1/app/parityanalytics/api/',
          requestParams,
          this.ajax.host,
        ),
        {
          method: 'GET',
          dataType: 'json',
          contentType: 'application/json; charset=utf-8',
        },
      );

      if (this.hotelAndMarketService.isDemoModeActive) {
        demoTransformTrendsData(trendsData);
      }

      return trendsData[params.segmentation];
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  beforeModel(transition: Transition): void {
    this.transition.validateParityInsightTrendsQueryParams(transition);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: RouteQueryParams) {
    return {
      trendsData: this.fetchTrendsDataTask.perform(params),
    };
  }
}
